import { ButtonIconPosition, ButtonVariant } from "@amzn/stencil-react-components/button";
import { IconCheck, IconPlus, IconSize } from "@amzn/stencil-react-icons";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { trackCustomEvent } from "@amzn/uxm-stencil-react-components/lib/src/uxm/custom-event";
import { COMPARE_ANALYTICS_EVENTS } from "common/context/compare";
import { getString } from "common/uistringlabels/uiStringUtils";
import { config } from "config";
import { AIForMeTool } from "lib/models/aiforme-models";
import React, { FC, useCallback } from "react";

export interface CompareButtonProps {
  /** The AI tool this button is for */
  tool: AIForMeTool;
  /** Whether the tool is currently selected for comparison */
  isSelected: boolean;
  /** Whether the button should be disabled (max tools reached and not selected) */
  disabled: boolean;
  /** Callback when tool is added to comparison */
  onAdd: (tool: AIForMeTool) => void;
  /** Callback when tool is removed from comparison */
  onRemove: (taxonomyId: string) => void;
}

/**
 * CompareButton component for tool cards
 * Allows users to add/remove tools from comparison selection
 */
export const CompareButton: FC<CompareButtonProps> = ({ tool, isSelected, disabled, onAdd, onRemove }) => {
  /**
   * Decides the action name for the compare button
   */
  const actionName = () => {
    if (isSelected) return "remove";
    if (disabled) return "disabled";
    return "add";
  };

  /**
   * Handle button click - toggle selection state
   */
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (isSelected) {
        onRemove(tool.taxonomyId);
      } else if (!disabled) {
        onAdd(tool);
      }
    },
    [isSelected, disabled, tool, onAdd, onRemove]
  );

  // Determine button variant and content based on state
  const variant = isSelected ? ButtonVariant.Primary : ButtonVariant.Secondary;
  const icon = isSelected ? (
    <IconCheck size={IconSize.ExtraSmall} aria-hidden="true" />
  ) : (
    <IconPlus size={IconSize.ExtraSmall} aria-hidden="true" />
  );
  const buttonText = isSelected ? getString("aiForMe.compare.selected") : getString("aiForMe.compare.add");

  return (
    <Button
      variant={variant}
      onClick={handleClick}
      disabled={disabled && !isSelected}
      data-testid={`compare-button-${tool.taxonomyId}`}
      touchpoint={COMPARE_ANALYTICS_EVENTS.TOGGLE}
      customData={{
        taxonomyId: tool.taxonomyId,
        title: tool.title,
        action: actionName(),
      }}
      aria-pressed={isSelected}
      aria-label={
        isSelected
          ? getString("aiForMe.compare.removeFromComparisonAriaLabel", { title: tool.title })
          : getString("aiForMe.compare.addToComparisonAriaLabel", { title: tool.title })
      }
      icon={icon}
      iconPosition={ButtonIconPosition.Trailing}
    >
      {buttonText}
    </Button>
  );
};

export default CompareButton;
